<template>
  <div id="pricing-page" :class="{ 'is-cleaning-up': isCleaningUpIncomplete }">
    <div v-if="isCleaningUpIncomplete" id="preloading">
      <div class="qr-loader-wrapper">
        <div class="qr-loader"></div>
      </div>
    </div>

    <!-- Alert for sub users -->
    <v-row v-if="!isTeamLead">
      <v-col cols="12" class="pt-8 pb-0">
        <AlertSubUserPayment />
      </v-col>
    </v-row>
    <!-- Alert for sub users END -->

    <!-- Page-Header -->
    <v-row v-if="!isUserOnAbtestVariantCNV3020()">
      <v-col xs="12">
        <h1 class="pricing__header text-h1 mt-6">{{ $t('fields.pricing_page.page_title') }}</h1>
        <h5 class="pricing__subheader text-h5">
          {{ $t('fields.pricing_page.page_subtitle') }}
        </h5>
      </v-col>
    </v-row>

    <CommonFeatures v-else />
    <!-- Page-Header END -->

    <!-- LAYOUT LOADING -->
    <template v-if="loadingSubscription || loadingPlans">
      <v-row>
        <v-col cols="12" class="text-center">
          <v-progress-circular indeterminate :width="3" color="candy" />
        </v-col>
      </v-row>
    </template>
    <!-- LAYOUT LOADING END -->

    <!-- Enterprise user -->
    <v-row v-if="isEnterprise && !loadingPlans && !loadingSubscription" class="mt-11">
      <v-col lg="10" offset-lg="1" class="py-0">
        <v-row align="stretch" justify="center">
          <v-col class="pt-0" sm="12">
            <EnterprisePlan />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <!-- Enterprise user END -->
    <template v-if="!isEnterprise && !loadingPlans && !loadingSubscription">
      <div class="mt-8 d-flex flex-column">
        <!-- Pricing table -->
        <v-row>
          <v-col lg="10" xl="10" offset-lg="1" offset-xl="1" class="py-0">
            <v-row align="stretch" justify="center">
              <v-col
                v-for="plan in gridPlans"
                :key="plan.id"
                class="pt-0"
                :class="[
                  'general-class',
                  {
                    'pricing__pricing-table--starter': plan.isStarterPlan,
                    'pricing__pricing-table--advanced': plan.planLevel === '2',
                    'pricing__pricing-table--professional': plan.planLevel === '3',
                  },
                ]"
                cols="12"
                sm="12"
                md="4"
                lg="4"
                xl="3"
              >
                <PlanTile
                  :plan="plan"
                  :monthly-plan-variant="monthlyVariantFor(plan)"
                  :stripe-subscription-service="stripeSubscriptionService"
                  :loading="isLoading"
                  @subscription-change-request="handleSubscriptionModalRequest"
                  @purchase-blocked-because-qr-quota="handlePurchaseBlockedBecauseQrQuota"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <!-- Pricing table END -->
      </div>

      <!-- Pricing table subtext -->
      <v-row align="center" justify="center">
        <v-col md="10" class="pricing__sub-note">
          <span v-if="!isUserOnAbtestVariantCNV3020()">{{ $t('fields.pricing_table.additional_features') }}</span>
          <br />
          {{ $t('fields.pricing_table.excluded_tax') }}
        </v-col>
      </v-row>
      <!-- Pricing table subtext END -->

      <!-- START: Enterprise promo -->
      <v-row class="pricing__enterprise container-fluid">
        <v-col cols="12" class="pa-0">
          <h3 class="text-h3 text-center">
            {{ $t('fields.pricing_page.enterprise_ad.title') }}
          </h3>
        </v-col>
        <v-col cols="12" class="pa-0">
          <p class="enterprise__text">
            {{ $t('fields.pricing_page.enterprise_ad.description') }}
          </p>
        </v-col>
        <v-col cols="12" class="text-center pa-0">
          <v-btn text large color="primary" class="mt-0" target="_blank" :ripple="false" :href="getInTouchLink">
            <span class="navy--text">{{ $t('fields.pricing_page.enterprise_ad.button') }}</span>
          </v-btn>
        </v-col>
      </v-row>
      <!-- Enterprise promo END -->
    </template>

    <!-- FAQ -->
    <v-row>
      <v-col lg="10" offset-lg="1" class="pb-0">
        <v-row class="pricing__faq">
          <v-col cols="12">
            <h3 class="text-h3 text-center mb-5">
              {{ $t('fields.pricing_page.enterprise_ad.button') }}
            </h3>
          </v-col>
          <v-col cols="12" lg="8">
            <v-expansion-panels class="faq__list" accordion :value="0">
              <v-expansion-panel
                v-for="faqEntry in faqList"
                :key="faqEntry.question"
                :data-test="faqEntry.freePlanAddition ? 'pricing-page-faq-entry-free-plan' : 'pricing-page-faq-entry'"
              >
                <v-expansion-panel-header class="faq__question" expand-icon="$chevron-system-small-outline">
                  {{ faqEntry.question }}
                </v-expansion-panel-header>
                <v-expansion-panel-content class="faq__answer">
                  {{ faqEntry.answer }}
                  <div v-if="faqEntry.freePlanAddition && accountIsTrial && !accountHasFreePlan">
                    {{ faqEntry.freePlanAddition }}
                    <v-btn
                      text
                      :ripple="false"
                      color="primary"
                      x-small
                      class="v-btn--text--link"
                      data-test="pricing-page-activate-free-plan"
                      @click.stop="clickOnActivateFreePlan"
                    >
                      <span class="navy--text">{{ $t('fields.pricing_page.faq.free_plan_activate_button') }}</span>
                    </v-btn>

                    <select-free-plan-modal :open="openSelectFreePlanModal" @cancel="onSelectFreePlanModalCancel" />
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>

          <v-col cols="12" lg="4">
            <v-row>
              <v-col cols="12" sm="6" lg="12" class="faq__bubble-wrapper">
                <div class="faq__speech-bubble">
                  <h5 class="text-h5 mb-3">
                    {{ $t('fields.pricing_page.faq.bubble_header') }}
                  </h5>
                  <v-btn
                    text
                    :ripple="false"
                    color="primary"
                    small
                    href="https://help.qr-code-generator.com/en"
                    target="_blank"
                  >
                    <span class="navy--text">{{ $t('fields.pricing_page.faq.bubble_helpcenter') }}</span>
                  </v-btn>
                </div>
              </v-col>

              <v-col cols="12" sm="6" lg="12" class="faq__illustration-wrapper">
                <img src="../../assets/illustrations/pricing-faq_illustration_exported.png" alt="" />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <!-- FAQ END -->
    <downgrade-restricted-modal
      :open="openDowngradeRestrictedModal"
      :new-plan="downgradeBlockedPlan || undefined"
      :user-code-amount="qrcodes.current"
      @cancel="onDowngradeRestrictedModalCancel"
    />
    <subscription-change-modal
      :open="subscriptionModalOpen"
      :plan="modalPlan"
      :intent="modalIntent"
      :plan-id="subUpgradeDowngradeModalPlanId"
      @cancel="handleModalCancel"
      @plan-change="handleModalPlanChange"
      @open="handleModalOpen"
      @code-limit-exceeded="handleCodeLimitExceeded"
    />
  </div>
</template>

<script lang="ts">
import AlertSubUserPayment from '@/components/Alerts/AlertSubUserPayment.vue';
import EnterprisePlan from '@/Pages/PricingPage/EnterprisePlan.vue';
import PlanTile from '@/Pages/PricingPage/PlanTile.vue';
import AppConfig from '@/AppConfig';
import EventService from '@/Services/EventService';
import OptimizelyService from '@/Services/OptimizelyService';
import { filterAndSortPlans } from '@/Services/Subscription/PlanViewService';
import StripeSubscriptionService from '@/Services/Subscription/StripeSubscriptionService';
import { Team, User } from '@/classes/auth';
import { Plan, Subscription, SubscriptionState } from '@/classes/stripe';
import CommonFeatures from '@/components/CommonFeatures.vue';
import DowngradeRestrictedModal from '@/components/Modals/Account/DowngradeRestrictedModal.vue';
import SelectFreePlanModal from '@/components/Modals/Account/SelectFreePlanModal.vue';
import SubscriptionChangeModal from '@/components/Modals/Subscription/SubscriptionChangeModal.vue';
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import store from '@/store';
import { AccountState } from '@/store/account';
import { Component, Watch, Vue } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';

@Component({
  components: {
    AlertSubUserPayment,
    DowngradeRestrictedModal,
    SelectFreePlanModal,
    EnterprisePlan,
    PlanTile,
    CommonFeatures,
    OptimizelyService,
    SubscriptionChangeModal,
  },
  computed: {
    ...mapState<SubscriptionState>('subscription', {
      subscriptionPlans: (state: SubscriptionState) => state.plans,
      loadingPlans: (state: SubscriptionState) => state.showLoading.loadingPlans,
      loadingSubscription: (state: SubscriptionState) => state.showLoading.loadingSubscription,
      activeSubscription: (state: SubscriptionState) => state.activeSubscription,
    }),
    ...mapState<AccountState>('account', {
      account: (state: AccountState) => state.account,
      qrcodes: (state: AccountState) => state.account?.qrcodes || [],
    }),
    ...mapGetters('auth', {
      user: 'user',
      team: 'team',
      isTeamLead: 'isTeamLead',
    }),
    ...mapGetters('subscription', {
      isEnterprise: 'isEnterprise', // <- for runtime
    }),
  },
})
export default class PlanSelection extends Vue {
  // state from vuex
  user!: User;
  team!: Team;
  loadingPlans!: boolean;
  loadingSubscription!: boolean;
  subscription: Subscription = new Subscription();
  subscriptionPlans!: Array<Plan>;
  activeSubscription!: Subscription;

  openDowngradeRestrictedModal = false;
  // Stores the plan that triggered a downgrade restriction so the modal can show the correct limits.
  downgradeBlockedPlan: Plan | null = null;
  openSelectFreePlanModal = false;
  getInTouchLink = 'https://form.asana.com/?k=0FszAJp6r2L5vtEiSrj9Ag&d=1174264937396';

  account!: any;
  qrcodes!: any;

  subscriptionModalOpen = false;
  modalPlan: Plan | null = null;
  modalIntent: 'upgrade' | 'downgrade' | null = null;
  subUpgradeDowngradeModalPlanId: string | null = null;

  // Used to display active subscription state inside plan selection
  stripeSubscriptionService: StripeSubscriptionService = new StripeSubscriptionService();

  // Loading indicator
  isLoading = false;

  // Full-page loading while incomplete subscription cleanup runs
  isCleaningUpIncomplete = false;

  // getter from vuex
  isEnterprise!: boolean;
  isTeamLead!: boolean;

  get faqList(): Array<{ question: string; answer: string; freePlanAddition?: string }> {
    return [
      {
        question: this.$t('fields.pricing_page.faq.question1').toString(),
        answer: this.$t('fields.pricing_page.faq.answer1').toString(),
      },
      {
        question: this.$t('fields.pricing_page.faq.question2').toString(),
        answer: this.$t('fields.pricing_page.faq.answer2').toString(),
      },
      {
        question: this.$t('fields.pricing_page.faq.question3').toString(),
        answer: this.$t('fields.pricing_page.faq.answer3').toString(),
      },
      {
        question: this.$t('fields.pricing_page.faq.question4').toString(),
        answer: this.$t('fields.pricing_page.faq.answer4').toString(),
      },
      {
        question: this.$t('fields.pricing_page.faq.question5').toString(),
        answer: this.$t('fields.pricing_page.faq.answer5').toString(),
        freePlanAddition: this.$t('fields.pricing_page.faq.free_plan_info').toString(),
      },
    ];
  }

  async created(): Promise<void> {
    this.isLoading = true;

    // Clean up any stale incomplete subscription (e.g. user abandoned PayPal via back button)
    if (this.$store.state.auth.user?.id) {
      this.isCleaningUpIncomplete = true;
      try {
        const { data: hasIncomplete } = await axios.get(
          AppConfig.getAPIBaseUrl() + '/subscription/stripe/has-incomplete'
        );
        if (hasIncomplete) {
          await axios.post(AppConfig.getAPIBaseUrl() + '/subscription/stripe/cancel-incomplete');
          // Force refresh subscription state so buttons reflect the cleanup
          await this.$store.dispatch('subscription/loadSubscription');
          // Clean up the URL so the upgrade/downgrade modal doesn't open
          if (this.$route.query.plan) {
            await this.$router.replace({
              query: {
                ...this.$route.query,
                plan: undefined,
              },
            });
          }
        }
      } catch (error) {
        Sentry.captureException(error);
      }
      this.isCleaningUpIncomplete = false;
    }

    await this.stripeSubscriptionService.initSubscription();
    this.isLoading = false;

    if (this.$route.query.trial === 'expired') {
      this.showTrialExpiredBanner();
    }

    // Deferred from the @Watch('$route') watcher so the upgrade/downgrade modal
    // only opens after incomplete subscription cleanup has finished
    this.subUpgradeDowngradeModalPlanId = this.getRequestedPlanFromRoute();

    EventService.createEvent(EventService.VISITED_PRICING);
  }

  showTrialExpiredBanner(): void {
    store.dispatch('layout/setSnackbar', {
      active: true,
      type: 'error',
      icon: 'warning-system',
      text: this.$t('fields.pricing_page.trial-expired-title').toString(),
      isSnackbar: false,
      sticky: true,
      showDismissButton: false,
      textClass: 'trial-expired-title',
    });
  }

  get accountIsTrial(): boolean {
    return !!this.account?.trial;
  }

  @Watch('$route', { deep: false })
  onRouteChanged(): void {
    this.subUpgradeDowngradeModalPlanId = this.getRequestedPlanFromRoute();
  }

  get accountHasFreePlan(): boolean {
    return this.account?.type === 'free';
  }

  get returnPlans(): Array<Plan> {
    return filterAndSortPlans(this.subscriptionPlans);
  }

  get gridPlans(): Array<Plan> {
    return this.returnPlans.filter((plan) => !plan.isMonthlyPlan);
  }

  // Lets a plan tile switch to the monthly plan of the same tier (Starter or Advanced, depending on the A/B test).
  monthlyVariantFor(plan: Plan): Plan | undefined {
    return this.returnPlans.find((candidate: Plan) => candidate.isMonthlyVariantOf(plan));
  }

  isUserOnAbtestVariantCNV3020(): boolean {
    return OptimizelyService.isUserOnAbtestVariantCNV3020();
  }

  /**
   * If the user can select a free plan, he will see the modal to proceed
   * If the user can't select a free plan, he will see a restriction modal
   */
  clickOnActivateFreePlan(): void {
    if (this.stripeSubscriptionService.canSelectFreePlan) {
      this.openSelectFreePlanModal = true;
    } else {
      // When free plan is blocked, ensure the restriction modal shows the generic copy
      this.downgradeBlockedPlan = null;
      this.openDowngradeRestrictedModal = true;
    }
  }

  /**
   * Called when a user clicks cancel in downgrade-restricted modal
   */
  onDowngradeRestrictedModalCancel(): void {
    this.openDowngradeRestrictedModal = false;
    this.downgradeBlockedPlan = null;
  }

  /**
   * Called when a user clicks cancel in free plan modal
   */
  onSelectFreePlanModalCancel(): void {
    this.openSelectFreePlanModal = false;
  }

  /**
   * Opens the shared subscription-change modal for a given plan/intent.
   * Called both from plan tiles (button clicks) and from deep-link logic.
   */
  handleSubscriptionModalRequest(payload: { plan: Plan; intent: 'upgrade' | 'downgrade' }): void {
    // Block downgrades where the new plan can't hold the existing QR codes
    if (payload.intent === 'downgrade' && !this.stripeSubscriptionService.canDowngradePlan(payload.plan.id)) {
      this.downgradeBlockedPlan = new Plan(payload.plan);
      this.openDowngradeRestrictedModal = true;
      return;
    }

    this.modalPlan = new Plan(payload.plan);
    this.modalIntent = payload.intent;
    this.subscriptionModalOpen = true;
  }

  /**
   * Shows the "downgrade" restriction modal when a purchase would exceed the new plan's limits.
   */
  handlePurchaseBlockedBecauseQrQuota(plan: Plan): void {
    this.downgradeBlockedPlan = new Plan(plan);
    this.openDowngradeRestrictedModal = true;
  }

  /**
   * The API refused the downgrade because the account has too many active dynamic codes, which the
   * client-side check missed (e.g. stale code counts). Show the restriction modal and reload the
   * account so the counts used by the pricing page are current again.
   */
  handleCodeLimitExceeded(plan: Plan): void {
    this.subscriptionModalOpen = false;
    this.handlePurchaseBlockedBecauseQrQuota(plan);
    this.$store.dispatch('account/loadAccount');
  }

  /**
   * Keeps the modal's local plan clone in sync when the order summary emits a plan change
   * (e.g. switching billing intervals inside the modal).
   */
  handleModalPlanChange(plan: Plan): void {
    this.modalPlan = new Plan(plan);
  }

  /**
   * Resets modal state when the dialog closes.
   */
  handleModalCancel(): void {
    this.subscriptionModalOpen = false;
    this.modalPlan = null;
    this.modalIntent = null;
  }

  /**
   * When modal determines an auto-open candidate, open it with the provided plan.
   */
  handleModalOpen(payload: { plan: Plan; intent: 'upgrade' | 'downgrade' }): void {
    this.handleSubscriptionModalRequest(payload);
  }

  /**
   * Extracts the plan query parameter (if any) from the current route.
   */
  private getRequestedPlanFromRoute(): string | null {
    const rawPlan = this.$route.query.plan;
    return typeof rawPlan === 'string' ? rawPlan : null;
  }
}
</script>

<style lang="scss">
#pricing-page.is-cleaning-up > *:not(#preloading) {
  display: none;
}

#pricing-page {
  .pricing__pricing-table--most-popular {
    @media (max-width: 959px) {
      margin-top: 30px;
    }
  }
  .pricing {
    &__header {
      text-align: center;
    }

    &__subheader {
      text-align: center;
      color: map-get($lapis, 'lighten-2');
      margin-top: 12px;
    }

    &__sub-note {
      padding: 0 12px;
      text-align: center;
      font-size: 12px;
      color: map-get($lapis, 'lighten-2');
    }

    // Enterprise ad
    &__enterprise {
      padding: 48px 12px;
      background: url('../../assets/illustrations/bg_image_enterprise.svg') no-repeat center;
      width: 100vw;
      position: relative;
      margin: 16px 0 16px -50vw;
      left: 50%;
      height: 264px;
      display: flex;
      justify-content: center;

      .enterprise {
        &__text {
          text-align: center;
          max-width: 528px;
          margin: 0 auto;
          color: map-get($lapis, 'lighten-2');
        }
      }
    }
  }

  // FAQ section
  .faq {
    &__list,
    &__speech-bubble {
      border: 2px solid map-get($neutral, 'lighten-2');
      border-radius: 12px;
    }

    &__question {
      font-size: 18px;
      line-height: 24px;
      color: map-get($lapis, 'lighten-1');
      font-weight: map-get($font-weights, 'semi-bold');
      padding: 12px 20px;

      &:not(.v-expansion-panel-header--active) {
        &:hover {
          background: rgba(map-get($primary, 'base'), 0.08);
        }

        &:focus {
          background: rgba(map-get($primary, 'base'), 0.16);
        }

        &:hover,
        &:focus {
          svg {
            color: map-get($primary, 'base');
          }
        }
      }

      svg {
        color: map-get($neutral, 'base');
      }
    }

    &__answer {
      color: map-get($lapis, 'lighten-2');
      font-size: 12px;
      line-height: 18px;

      .v-expansion-panel-content__wrap {
        padding: 0 20px 16px;
      }
    }

    &__speech-bubble {
      padding: 16px 20px;
    }

    &__illustration-wrapper {
      @media (min-width: 600px) and (max-width: 1263px) {
        display: flex;
        justify-content: center;
      }
    }

    &__bubble-wrapper {
      padding-top: 0;

      @media (min-width: 600px) and (max-width: 1263px) {
        order: 2;
      }
    }
  }
}
.v-snack {
  &__wrapper,
  &__content {
    width: auto;
    max-width: 100%;
  }
}
</style>
